import React, { useCallback, useState } from 'react';
import { Alert, Slide, Snackbar } from '@ui/components';
import { AlertItem, Context } from './types';
import { useBodyChild, useObjectMemo } from '../../hooks';
import { createPortal } from 'react-dom';

const AlertContext = React.createContext<Context>({
  show: () => undefined,
  showSuccess: () => undefined,
  showError: () => undefined,
});

export const AlertProvider: React.FC = (props) => {
  const [[alert], setAlertState] = useState<AlertItem[]>([]);
  const [hide, setHide] = useState(false);
  const el = useBodyChild('div');

  const setAlerts: typeof setAlertState = (item) => {
    setAlertState(item);
    setHide(false);
  };

  const show: Context['show'] = useCallback((alert) => setAlerts((a) => [...a, alert]), []);
  const showSuccess = useCallback((text: string) => show({ text, severity: 'success' }), [show]);
  const showError = useCallback((text: string) => show({ text, severity: 'error' }), [show]);

  const value = useObjectMemo({ show, showSuccess, showError });

  return (
    <>
      <AlertContext.Provider value={value} {...props} />

      {createPortal(
        <Snackbar
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          TransitionComponent={Slide}
          TransitionProps={{
            onExited: () => setAlerts((a) => a.slice(1)),
          }}
          autoHideDuration={3000}
          open={!!alert && !hide}
          onClose={() => setHide(true)}
        >
          <Alert variant='filled' severity={alert?.severity}>
            {alert?.text}
          </Alert>
        </Snackbar>,
        el,
      )}
    </>
  );
};

export const useAlerts = () => React.useContext(AlertContext);
